import React, { useEffect, useState } from "react";
import { api, copyText, API } from "@/lib/api";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { SectionTitle } from "@/components/SectionTitle";
import { ShareNetwork } from "@phosphor-icons/react";

const cols = [
  { key: "gp", label: "GP" },
  { key: "w", label: "W" },
  { key: "l", label: "L" },
  { key: "pf", label: "Total Score" },
];

function DivisionTable({ division, rows }) {
  const share = async () => {
    if (await copyText(`${API}/share/standings?division=${encodeURIComponent(division)}`)) toast.success(`${division} standings link copied!`);
    else toast.error("Could not copy link");
  };
  return (
    <div className="mb-12" data-testid={`division-${division.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-3 mb-4">
        <span className="w-2.5 h-7 bg-[#FF5722]" />
        <h3 className="font-display text-3xl uppercase">{division} Division</h3>
        <button onClick={share} data-testid={`share-standings-${division.replace(/\s+/g, "-").toLowerCase()}`} className="ml-auto flex items-center gap-1.5 border border-[#FF5722]/50 text-[#FF5722] hover:bg-[#FF5722]/10 uppercase tracking-wide text-xs font-bold px-3 py-1.5 transition-all duration-200">
          <ShareNetwork size={14} weight="bold" /> Share
        </button>
      </div>
      <div className="border border-white/10 bg-[#121212] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-white/50 uppercase text-xs tracking-wider">
              <th className="text-left py-4 px-5 w-12">#</th>
              <th className="text-left py-4 px-5">Team</th>
              {cols.map((c) => (
                <th key={c.key} className="text-center py-4 px-3 tabular-nums">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-white/40">No teams in this division yet.</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={r.team_id} data-testid="standings-row" className="border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
                <td className="py-4 px-5 font-display text-2xl text-[#FF5722] tabular-nums">{i + 1}</td>
                <td className="py-4 px-5">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 flex items-center justify-center text-black font-display text-sm overflow-hidden shrink-0" style={{ background: r.primary_color || "#FF5722" }}>
                      {r.logo_url ? <img src={r.logo_url} alt="" className="w-full h-full object-cover" /> : r.team_name.charAt(0)}
                    </span>
                    <div className="min-w-0">
                      <span className="font-semibold uppercase tracking-wide">{r.team_name}</span>
                      {r.h2h_note && (
                        <p className="text-[11px] text-white/40 mt-0.5" data-testid={`standings-h2h-${r.team_id}`}>{r.h2h_note}</p>
                      )}
                    </div>
                  </div>
                </td>
                {cols.map((c) => (
                  <td key={c.key} className="text-center py-4 px-3 tabular-nums text-white/80">{r[c.key]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Standings() {
  const { settings } = useSettings();
  const DIVISIONS = settings.divisions || [];
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/standings").then((r) => setRows(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 py-16" data-testid="standings-page">
      <SectionTitle overline="The race" title="Standings" sub="Auto-calculated from completed game results. Ranked by wins, then head-to-head between tied teams, then total points scored." />

      {DIVISIONS.map((d) => (
        <DivisionTable key={d} division={d} rows={rows.filter((r) => (r.division || "Open") === d)} />
      ))}
    </div>
  );
}
